import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Grid,
  Tooltip,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Switch
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { AutomationOptions } from 'components/Template';
import InfoIcon from '@material-ui/icons/Info';
import { CloseIcon } from 'components';
import { ProductType } from 'constants.js';

const useStyles = makeStyles(theme => ({
  root: {},
  cardHeader: {},
  radioLabel: {
    marginLeft: '0.7rem',
    alignItems: 'baseline'
  },
  radioGroup: {
    marginLeft: '1rem'
  },
  cardActions: {
    flexDirection: 'row-reverse'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  segmentation: {
    display: 'flex',
    flexDirection: 'row',
    maxWidth: '100%',
    columnGap: '12px',
    marginTop: '12px',
  },
  autoComplete: {
    '& .MuiInputBase-root': {
        padding: '6px'
    },
    '& .MuiInputBase-input': {
        padding: '5.5px 4px !important'
    }
  },
  checkedIcon: {
    color: theme.palette.primary
  },
  error: {
    color: theme.palette.error.main
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between'
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    width: '95%'
  }
}));

const Details = props => {
  const {
    className,
    staticContext,
    template,
    next,
    showNav,
    isPopupDialog,
    ...rest
  } = props;
  const {
    isLoading,
    goNext,
    thisTemplate,
    isQueryReadOnly,
    closeQuery,
    handleChange,
    toggleEventSegmentation,
    handleProductSegmentationChange,
    onFocus,
    onBlur,
    getErrorMessage,
    shouldShowError,
    updateCurrentErrorValidationSegment
  } = template;

  useEffect(() => {
    updateCurrentErrorValidationSegment('details');
  }, [thisTemplate, updateCurrentErrorValidationSegment]);

  const classes = useStyles();

  const areInitialSettingsDisabled = () =>
    isTemplateSaved() ||
    !!thisTemplate.jsonContent ||
    thisTemplate.conditionExpression?.subConditionExpressions?.length > 0;

  const isTemplateSaved = () => (thisTemplate.id && thisTemplate.id.length > 0) || (thisTemplate.uuid && thisTemplate.uuid.length > 0);
  
  const productIntegrationOptions = useSelector(
    store => store.events.supportedEventTypes.productIntegrationOptions
  );

  const [products, setProducts] = React.useState([]);

  useEffect(() => {
    if (!productIntegrationOptions) return;
    setProducts([
      {
        displayName: 'All Products',
        value: 'ALL'
      },
      ...productIntegrationOptions.filter(option => option.type === "PRODUCT_NAME").map(option => ({ displayName: option.displayName, value: option.displayName }))
    ]);
  }, [productIntegrationOptions]);

  const handleCancellationChange = (event) => {
    handleChange({
      target: {
        name: 'isCancellationInclude',
        value: event.target.checked
      }
    });
  }

  return (
    <div className={classes.root}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          {!isPopupDialog && (
            <div className={classes.headerContainer}>
              <div className={classes.title}>
                <CardHeader
                  subheader="* indicates a required field"
                  title="Recipient Query Details"
                  titleTypographyProps={{ variant: 'h1' }}
                />
                {isQueryReadOnly() && (
                  <Tooltip
                    title={
                      "You do not have access to edit this query. Tenant Default queries can be edited only with 'ALL' property."
                    }>
                    <InfoIcon size="small" style={{ marginTop: '26px' }} />
                  </Tooltip>
                )}
              </div>
              <CloseIcon onClickClose={closeQuery} />
            </div>
          )}
          <Divider />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <Grid item xs={12} className={classes.inputGrid}>
                  <TextField
                    fullWidth
                    label="Recipient Query Name"
                    margin="dense"
                    name="name"
                    onChange={handleChange}
                    required
                    disabled={isQueryReadOnly()}
                    value={thisTemplate.name || ''}
                    variant="outlined"
                    onFocus={onFocus}
                    onBlur={onBlur}
                    error={shouldShowError('name')}
                    helperText={getErrorMessage('name')}
                  />
                </Grid>

                <AutomationOptions
                  isPopupDialog={isPopupDialog}
                  template={template}
                  type="queryTemplate"
                  disabled={areInitialSettingsDisabled()}
                />

                {thisTemplate.productType === ProductType.List && (
                  <Grid item xs={12} className={classes.segmentation}>
                    <div>
                      <FormControlLabel
                        style={{ margin: '0px' }}
                        label="Reservation Segmentation"
                        control={
                          <Checkbox
                            checked={thisTemplate.eventSegmentation?.segmentAgainstEvent || false}
                            onChange={toggleEventSegmentation}
                            color="primary"
                            value="true"
                            disabled={isQueryReadOnly()}
                          />
                        }
                      />
                    </div>
                    <div>
                      <Tooltip title="If this checkbox is enabled, profiles that are already part of event history will be excluded from the query results.">
                          <InfoIcon size="small" style={{ marginTop: '8px' }} />
                      </Tooltip>
                    </div>
                  </Grid>
                )}

                {thisTemplate.eventSegmentation?.segmentAgainstEvent && products.length > 0 && (
                  <Grid item xs={12}  md={6} className={classes.segmentation}>
                    <Autocomplete
                      id="productSegmentation"
                      options={products}
                      className={classes.autoComplete}
                      disabled={isQueryReadOnly()}
                      getOptionLabel={(option) => option.displayName || option.value}
                      renderOption={(option) => option.displayName || option.value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Exclude product"
                          className={classes.searchInput}
                          onFocus={onFocus}
                          onBlur={onBlur}
                          error={shouldShowError('eventSegmentation.productSegmentation')}
                          helperText={getErrorMessage('eventSegmentation.productSegmentation')}
                          disabled={isQueryReadOnly()}
                        />
                      )}
                      value={
                        products.find(
                          option => option.value === thisTemplate.eventSegmentation?.productSegmentation
                        )
                      }
                      onChange={(event, newValue) => {
                        handleProductSegmentationChange(newValue);
                      }}
                      style={{ width: "50%" }}
                    />
                    <div>
                      <Tooltip title="If this field is populated, profiles associated under event history of this product will be excluded from the query results.">
                        <InfoIcon size="small" style={{ marginTop: '8px' }} />
                      </Tooltip>
                    </div>
                  </Grid>
                )}

                {thisTemplate.productType === 'EVENT' && (
                  <Grid item xs={12} className={classes.segmentation}>
                    <div>
                      <FormControlLabel
                        style={{ margin: '0px' }}
                        label="Include Cancellation"
                        control={
                          <Switch
                            className={classes.switch}
                            checked={!!thisTemplate.isCancellationInclude}
                            name="isCancellationInclude"
                            onChange={handleCancellationChange}
                            disabled={isQueryReadOnly()}
                            color="primary"
                            inputProps={{ 'aria-label': 'cancellation toggle' }}
                          />
                        }
                      />
                    </div>
                    <div>
                      <Tooltip title="If toggle on, cancelled events are included">
                        <InfoIcon size="small" style={{ marginTop: '8px' }} />
                      </Tooltip>
                    </div>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </CardContent>
          {(next || showNav) && (
            <>
              <Divider />
              <CardActions className={classes.cardActions}>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={isLoading}
                  link={next}
                  onClick={goNext}>
                  Next
                </Button>
              </CardActions>
            </>
          )}
        </form>
      </Card>
    </div>
  );
};

export default Details;
